import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'

interface HoldHintApi {
  /** A hold button mounted. Returns its unmount callback. */
  register: () => () => void
  /** Somebody tapped a hold button and let go before it armed. */
  nudge: () => void
}

const HoldHintContext = createContext<HoldHintApi | null>(null)

/** Milliseconds a hold control may sit untouched before we explain itself. */
const IDLE_MS = 10_000
const VISIBLE_MS = 7000

/**
 * "Hold, do not tap" is not discoverable on a phone - the first instinct is a
 * tap, nothing happens, and people assume the button is broken. So: explain it
 * the moment somebody taps and releases, and otherwise after ten seconds of a
 * hold control sitting there untouched.
 */
export function HoldHintProvider({ children }: { children: ReactNode }) {
  const [mounted, setMounted] = useState(0)
  const [visible, setVisible] = useState(false)
  // One explanation per run of hold controls; it resets when the phase changes
  // and the buttons come and go.
  const explained = useRef(false)

  const register = useCallback(() => {
    setMounted((n) => n + 1)
    return () => setMounted((n) => n - 1)
  }, [])

  const nudge = useCallback(() => {
    explained.current = true
    setVisible(true)
  }, [])

  useEffect(() => {
    if (mounted === 0) {
      explained.current = false
      setVisible(false)
      return
    }
    if (explained.current) return
    const timer = window.setTimeout(() => {
      explained.current = true
      setVisible(true)
    }, IDLE_MS)
    return () => window.clearTimeout(timer)
  }, [mounted])

  useEffect(() => {
    if (!visible) return
    const timer = window.setTimeout(() => setVisible(false), VISIBLE_MS)
    return () => window.clearTimeout(timer)
  }, [visible])

  const api = useMemo(() => ({ register, nudge }), [register, nudge])

  return (
    <HoldHintContext.Provider value={api}>
      {children}
      {visible && mounted > 0 && (
        <div className="holdhint" role="status" data-testid="hold-hint">
          <span className="holdhint__icon" aria-hidden="true">
            <span className="holdhint__finger" />
          </span>
          <span className="holdhint__text">
            <strong>Press and hold</strong> the button until it fills. A single tap does nothing —
            that is deliberate, so a fumble cannot cast your vote.
          </span>
          <button
            type="button"
            className="holdhint__close"
            aria-label="dismiss"
            onClick={() => setVisible(false)}
          >
            ✕
          </button>
        </div>
      )}
    </HoldHintContext.Provider>
  )
}

/** Null outside a provider - table screens have no hold controls. */
export function useHoldHint(): HoldHintApi | null {
  return useContext(HoldHintContext)
}
